/**
 * Phone number formatting utilities
 * 
 * Examples of formatted output:
 * - 7 digits: "123-4567"
 * - 8 digits: "(123) 456-78"
 * - 9 digits: "(123) 456-789"
 * - 10 digits: "(123) 456-7890"
 * - 11 digits: "(123) 456-78901"
 * - 12+ digits: "(123) 456-789012..."
 */

/**
 * Formats a phone number to readable format with spaces/dashes
 * @param value - Raw phone number string
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, '');

  // Format based on length
  if (phoneNumber.length === 0) {
    return '';
  } else if (phoneNumber.length <= 3) {
    return phoneNumber;
  } else if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else if (phoneNumber.length === 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  } else if (phoneNumber.length === 10) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  } else if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
    // Handle 11-digit numbers starting with 1 (US country code)
    return `+1 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7)}`;
  } else {
    // For any other length, format as (xxx) xxx-xxxxx...
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  }
};

/**
 * Validates phone number format - accepts reasonable phone number lengths
 * @param phone - Phone number string to validate
 * @returns true if valid format, false otherwise
 */
export const validatePhoneNumber = (phone: string): boolean => {
  if (!phone) return false;

  const digits = phone.replace(/\D/g, '');

  // Require exactly 10 digits for US numbers, or 11 digits starting with 1 (country code)
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith('1')) return true;
  return false;
};
